import { UserPermissions } from '../services/permission.service';

// Core User and Authentication
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  department: string;
  position: string;
  avatar?: string;
  permissions: UserPermissions;
  createdAt: Date;
  updatedAt: Date;
}

export enum HiringStatus {
  NEW = 'NEW',
  SHORTLISTED = 'SHORTLISTED',
  SELECTED_FOR_INTERVIEW = 'SELECTED_FOR_INTERVIEW',
  REJECTED = 'REJECTED',
  INTERVIEW_SCHEDULED = 'INTERVIEW_SCHEDULED',
  INTERVIEW_COMPLETED = 'INTERVIEW_COMPLETED',
  CANDIDATE_SELECTED = 'CANDIDATE_SELECTED',
  CANDIDATE_REJECTED = 'CANDIDATE_REJECTED',
  OFFER_SENT = 'OFFER_SENT',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  OFFER_REJECTED = 'OFFER_REJECTED',
  HIRED = 'HIRED',
  FUTURE_REFERENCE = 'FUTURE_REFERENCE',
  ON_HOLD = 'ON_HOLD',
}

export enum JobStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export enum InterviewStatus {
  SCHEDULED = 'SCHEDULED',
  CONFIRMED = 'CONFIRMED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  RESCHEDULED = 'RESCHEDULED',
}

// Personal Data Structure
export interface PersonalData {
  personalInfo?: {
    displayName?: string;
    middleName?: string;
    gender?: string;
    dateOfBirth?: Date;
    maritalStatus?: string;
    nationality?: string;
    bloodGroup?: string;
    placeOfBirth?: string;
    companyName?: string;

  };
  familyInfo?: {
    familyMembers?: Array<{
      name: string;
      relationship: string;
      dateOfBirth?: Date;
      occupation?: string;
      mobile: string;
      aadhaar: string;
    }>;
  };
  addressInfo?: {
    currentAddress1?: string;
    currentAddress2?: string;
    currentCity?: string;
    currentCountry?: string;
    currentState?: string;
    currentPinCode?: string;
    sameAsCurrentAddress?: boolean;
    permanentAddress1?: string;
    permanentAddress2?: string;
    permanentCity?: string;
    permanentCountry?: string;
    permanentState?: string;
    permanentPinCode?: string;
  };
  educationEntries?: Array<{
    _id?: string;
    degree: string;
    collegeName: string;
    specialization: string;
    startDate: Date;
    endDate: Date;
  }>;
  experienceDetails?: {
    experienceType?: string;
    totalYears: number;
    totalMonths: number;
    previousEmployers?: Array<{
      companyName: string;
      department?: string;
      jobTitle: string;
      startDate: Date;
      endDate: Date;
    }>;
  };
}

// Employee Management
export interface Employee extends User {
  employeeId: string;
  joiningDate: Date;
  salary: number;
  managerId: string;
  status: 'active' | 'on_leave' | 'terminated';
  documents: EmployeeDocument[];
  jobInfo?: JobInfo;
  agreementInfo?: AgreementInfo;
  compensationInfo?: CompensationInfo;
  personal?: PersonalData;
  banking?: Array<{
    countryName: string;
    accountHolderName: string;
    bankName: string;
    bankBranch: string;
    accountNumber: string;
    accountType: string;
    attachments: string[];
    ifscCode: string;
    ibanCode: string;
    bankCode: string;
    branchCode: string;
    swiftCode: string;
    status: string;
  }>;

  customFields: Record<string, any>;
}

export interface EmployeeDocument {
  id: string;
  type: 'id_proof' | 'resume' | 'certificate' | 'other';
  name: string;
  url: string;
  uploadedAt: Date;
}

export interface JobInfo {
  joiningDate?: Date;
  location?: string;
  jobTitle?: string; // This will be the job title ID
  category?: string;
  reportingManager?: string;
  lineManager?: string;
  lineManager2?: string;
  // Virtual populate fields from backend
  reportingManagers?: { _id: string; firstName: string; lastName: string; employeeId: string; email: string }[];
  lineManagers?: { _id: string; firstName: string; lastName: string; employeeId: string; email: string }[];
  lineManagers2?: { _id: string; firstName: string; lastName: string; employeeId: string; email: string }[];
  employmentStatus?: string;
  probationEndDate?: Date;
  notifyDay?: number;
  note?: string;
}

export interface AgreementInfo {
  startDate?: Date;
  endDate?: Date;
  periodInMonths?: string;
  notifyPeriodInDays?: string;
  note?: string;
}

export interface Agreement {
  _id?: string;
  startDate: Date | string;
  endDate: Date | string;
  periodInMonths: string;
  notifyPeriodInDays: string;
  note: string;
  employee: {
    _id: string;
    user: {
      firstName: string;
      lastName: string;
      email: string;
      employeeId: string;
    };
    department?: {
      _id?: string;
      id?: string;
      name?: string;
    } | string;
  };
}

export interface CompensationInfo {
  startDate?: Date;
  endDate?: Date;
  newSalary?: string;
  payFrequency?: string;
  noticePeriodInDays?: string;
  notifyPeriodInDays?: string;
  salaryMode?: string;
  note?: string;
}

export interface Compensation {
  _id: string;
  startDate: Date | string;
  endDate: Date | string;
  newSalary: string;
  payFrequency: string;
  noticePeriodInDays: string;
  notifyPeriodInDays: string;
  salaryMode: string;
  note: string;
  employee: {
    _id: string;
    user: {
      firstName: string;
      lastName: string;
      email: string;
      employeeId: string;
    };
    department?: {
      _id?: string;
      id?: string;
      name?: string;
    } | string;
    profilePicture?: string;
  };
}



export interface Department {
  id: string;
  name: string;
  managerId: string;
  description: string;
  employeeCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Attendance {
  id: string;
  employeeId: string;
  date: Date;
  checkIn: Date;
  checkOut: Date;
  status: 'present' | 'absent' | 'late' | 'half_day';
  workHours: number;
  notes?: string;
}

export interface LeaveRequest {
  id: string;
  employeeId: string;
  type: 'annual' | 'sick' | 'maternity' | 'unpaid';
  startDate: Date;
  endDate: Date;
  status: 'pending' | 'approved' | 'rejected';
  reason: string;
  approvedBy?: string;
  approvedAt?: Date;
  documents?: string[];
}

export interface PerformanceReview {
  id: string;
  employeeId: string;
  reviewerId: string;
  period: string;
  rating: number;
  goals: PerformanceGoal[];
  feedback: string;
  status: 'draft' | 'submitted' | 'reviewed';
  createdAt: Date;
  updatedAt: Date;
}

export interface PerformanceGoal {
  id: string;
  title: string;
  description: string;
  targetDate: Date;
  status: 'pending' | 'in_progress' | 'completed';
  progress: number;
}

// Project Management
export interface ProjectEmployee {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  clientId: string;
  managerId: string;
  startDate: Date;
  endDate: Date;
  status: 'planning' | 'active' | 'on_hold' | 'completed';
  budget: number;
  team: ProjectTeamMember[];
  tasks: Task[];
  documents: ProjectDocument[];
  customFields: Record<string, any>;
}

export interface ProjectApiResponse {
  _id: string;
  projectName: string;
  client: string;
  projectDescription: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  status: 'PLANNING' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED';
  startDate: string;
  endDate: string;
  projectManager: ProjectEmployee;
  teamMembers: ProjectEmployee[];
  budget: number;
  progressPercentage: number;
  createdAt: string;
  updatedAt: string;
  createdBy: ProjectEmployee;
  updatedBy: ProjectEmployee;
  isActive: boolean;
  isDeleted: boolean;
  __v: number;
  timeline: TimelineEntry[];
}

export interface ProjectTeamMember {
  employeeId: string;
  role: string;
  allocation: number;
  startDate: Date;
  endDate?: Date;
}

export interface Task {
  id: string;
  projectId: string;
  title: string;
  description: string;
  assigneeId: string;
  status: 'todo' | 'in_progress' | 'in_review' | 'done';
  priority: 'low' | 'medium' | 'high';
  startDate: Date;
  dueDate: Date;
  estimatedHours: number;
  actualHours?: number;
  dependencies: string[];
  attachments: string[];
  customFields: Record<string, any>;
}

export interface Sprint {
  id: string;
  projectId: string;
  name: string;
  startDate: Date;
  endDate: Date;
  status: 'planning' | 'active' | 'completed';
  velocity: number;
  tasks: string[];
  burndownData: BurndownData[];
}

export interface BurndownData {
  date: Date;
  remainingPoints: number;
}

// Project API Request/Response Types
export interface CreateProjectRequest {
  projectName: string;
  client: string;
  projectDescription: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  status: 'PLANNING' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED';
  startDate: string;
  endDate: string;
  projectManager: string;
  teamMembers: string[];
  budget?: number;
  progressPercentage?: number;
}

export interface UpdateProjectRequest {
  projectName?: string;
  client?: string;
  projectDescription?: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH';
  status?: 'PLANNING' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED';
  startDate?: string;
  endDate?: string;
  projectManager?: string;
  teamMembers?: string[];
  budget?: number;
  progressPercentage?: number;
}

export interface ProjectFilters {
  status?: string;
  priority?: string;
  clientId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  myProjects?: boolean;
}

export interface ProjectsResponse {
  data: ProjectApiResponse[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Sales & Clients
export interface Client {
  id: string;
  name: string;
  type: 'individual' | 'company';
  industry: string;
  status: 'active' | 'inactive' | 'lead';
  contacts: ClientContact[];
  projects: string[];
  contracts: string[];
  billingInfo: BillingInfo;
  createdAt: Date;
  updatedAt: Date;
  customFields: Record<string, any>;
}

export interface ClientContact {
  id: string;
  name: string;
  position: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

export interface BillingInfo {
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  taxId?: string;
  paymentTerms: string;
}

export interface Proposal {
  id: string;
  clientId: string;
  title: string;
  description: string;
  value: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected';
  validUntil: Date;
  items: ProposalItem[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProposalItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Contract {
  id: string;
  clientId: string;
  proposalId: string;
  type: 'fixed_price' | 'recurring' | 'subscription' | 'time_materials';
  startDate: Date;
  endDate: Date;
  value: number;
  status: 'active' | 'expired' | 'terminated';
  terms: string;
  documents: string[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// Asset Management
export interface Asset {
  id: string;
  name: string;
  type: 'hardware' | 'software' | 'furniture' | 'other';
  category: string;
  status: 'active' | 'maintenance' | 'retired';
  assignedTo?: string;
  purchaseDate: Date;
  purchasePrice: number;
  warrantyExpiry?: Date;
  location: string;
  specifications: Record<string, any>;
  maintenanceHistory: MaintenanceRecord[];
  customFields: Record<string, any>;
}

export interface License {
  id: string;
  name: string;
  type: 'software' | 'subscription' | 'service';
  vendor: string;
  startDate: Date;
  endDate: Date;
  cost: number;
  status: 'active' | 'expired' | 'cancelled';
  seats: number;
  usedSeats: number;
  assignedTo: string[];
  renewalDate: Date;
}

export interface MaintenanceRecord {
  id: string;
  assetId: string;
  type: 'preventive' | 'corrective';
  description: string;
  date: Date;
  cost: number;
  performedBy: string;
  nextMaintenanceDate?: Date;
  status: 'scheduled' | 'completed' | 'cancelled';
}

// Finance
export interface Invoice {
  id: string;
  clientId: string;
  contractId: string;
  number: string;
  issueDate: Date;
  dueDate: Date;
  amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  items: InvoiceItem[];
  payments: Payment[];
  notes?: string;
  customFields: Record<string, any>;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Payment {
  id: string;
  invoiceId: string;
  amount: number;
  date: Date;
  method: 'bank_transfer' | 'credit_card' | 'check';
  status: 'pending' | 'completed' | 'failed';
  reference: string;
}

export interface Expense {
  id: string;
  category: string;
  amount: number;
  date: Date;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  receipt?: string;
  projectId?: string;
}

// Settings
export interface Role {
  _id: string;
  id: string;
  name: string;
  description: string;
  permissions: Record<string, any>;
  isActive?: boolean;
  isSystem?: boolean; // Backend flag indicating if this is a system role
  createdAt: Date;
  updatedAt: Date;
  pending?: boolean;
}

export interface SystemSetting {
  id: string;
  key: string;
  value: string | number | boolean | object;
  description: string;
  isPublic: boolean;
  updatedBy: string;
  updatedAt: string;
}

export interface ProjectDocument {
  id: string;
  name: string;
  type: 'contract' | 'proposal' | 'report' | 'other';
  url: string;
  uploadedBy: string;
  uploadedAt: Date;
  version: string;
  description?: string;
}

// Role and Permission Models
export interface Permission {
  id: string;
  name: string;
  description: string;
  module: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'manage';
  createdAt: string;
  updatedAt: string;
}

export interface UserRole {
  id: string;
  userId: string;
  roleId: string;
  assignedBy: string;
  assignedAt: string;
}

// Custom Field Models
export interface CustomField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'file';
  entity: 'client' | 'project' | 'task' | 'employee' | 'asset' | 'invoice';
  isRequired: boolean;
  options?: string[]; // For select/multiselect fields
  defaultValue?: string | number | boolean;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Status Configuration Models
export interface StatusConfig {
  id: string;
  name: string;
  category: 'project' | 'task';
  color: string;
  isDefault: boolean;
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

// Workflow Models
export interface WorkflowStep {
  id: string;
  name: string;
  statusId: string;
  order: number;
  canBeReassigned: boolean;
  requiredFields: string[];
  notifications: {
    type: 'email' | 'in-app';
    template: string;
    recipients: string[];
  }[];
}

export interface Workflow {
  id: string;
  name: string;
  type: 'client' | 'project' | 'task' | 'invoice' | 'asset';
  steps: WorkflowStep[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Notification Template Models
export interface NotificationTemplate {
  id: string;
  name: string;
  type: 'email' | 'in-app';
  subject: string;
  body: string;
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

// Audit Log Models
export interface AuditLog {
  id: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string;
  changes: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  metadata: Record<string, any>;
  createdAt: string;
}

// Recruitment Management
export interface Candidate {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  currentCompany?: string;
  currentRole?: string;
  experienceYears: number;
  expectedSalary?: string;
  currentSalary?: string;
  noticePeriod?: string;
  status: string;
  resumeLink?: string;
  resumeFileName?: string; // Original filename of the uploaded resume
  department?: string | null | { _id: string; name: string };
  position?: string; // Added for job position
  linkedinUrl?: string;
  reasonForLeavingPreviousRole?: string;
  communicationSkills?: string; // Added communication skills field

  source?: string | null | { _id: string; name: string };
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  timeline: TimelineEntry[];
  feedback?: string;
  location?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TimelineEntry {
  _id: string;
  type: 'candidate' | 'interview' | 'feedback' | 'status_change' | 'onboarding_tasks_assigned';
  subType: string;
  title: string;
  description: string;
  notes?: string;
  metadata?: Record<string, any>;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
}

export interface CandidateFilters {
  search?: string;
  status?: string;
  department?: string;
  jobPosting?: string;
  createdBy?: string;
  experienceYears?: number;
  expectedSalaryMin?: number;
  expectedSalaryMax?: number;
  currentSalaryMin?: number;
  currentSalaryMax?: number;
  linkedinUrl?: string;

  reasonForLeaving?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface CandidatesResponse {
  data: Candidate[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface CreateCandidateRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  currentCompany?: string;
  currentRole?: string;
  experienceYears: number;
  expectedSalary?: string;
  currentSalary?: string;
  noticePeriod?: string;
  resumeLink?: string;
  resumeFileName?: string; // Original filename of the uploaded resume
  department?: string;
  source?: string;
  position?: string; // Added for job position
  linkedinUrl?: string;
  reasonForLeavingPreviousRole?: string;
  communicationSkills?: string; // Added communication skills field

  location?: string;
  notes?: string;
}

export interface UpdateCandidateRequest extends Partial<CreateCandidateRequest> {
  status?: string; // Changed from Candidate['status'] to string to support dynamic statuses
  statusNotes?: string;
  feedback?: string;
}

export interface CandidateStats {
  totalCandidates: number;
  newCandidates: number;
  inProcess: number;
  hired: number;
  rejected: number;
  statusDistribution: { [key: string]: number };
  departmentDistribution: { [key: string]: number };
}

// Interview Management
export interface Interview {
  _id: string;
  candidate: string; // candidate id
  department: string; // department id
  interviewType: 'technical' | 'behavioural' | 'portfolio_review' | string;
  interviewDate: string; // YYYY-MM-DD
  interviewTime: string; // HH:mm
  interviewers: string[]; // interviewer ids
  location: string;
  notes?: string;
  status?: 'Scheduled' | 'Confirmed' | 'Completed' | 'Cancelled' | 'Rescheduled';
  timeline?: Array<{
    date: string;
    status: string;
    description: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInterviewRequest {
  candidate: string;
  department?: string;
  interviewType: string;
  interviewDate: string;
  interviewTime: string;
  interviewers: string[];
  location?: string;
  notes?: string;
  roundName?: string;
}

export interface UpdateInterviewRequest extends Partial<CreateInterviewRequest> {
  status?: 'Scheduled' | 'Confirmed' | 'Completed' | 'Cancelled' | 'Rescheduled';
}

// Onboarding Management
export interface OnboardingTask {
  _id: string;
  id?: string;
  title: string;
  description: string;
  category: string;
  dueDate?: string;
  status?: 'pending' | 'in_progress' | 'completed';
  weight?: number;
  isActive?: boolean;
  createdAt?: string;
  createdBy?: string | {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string;
  };
  updatedAt?: string;
}

export interface OnboardingTaskFormData {
  title: string;
  description?: string;
  category?: string;
  dueDate?: string;
}

// Timesheet Management
export interface TimesheetEntry {
  _id: string;
  taskId: string;
  employeeId: string;
  startTime: string;
  endTime?: string;
  notes?: string;
  duration?: number; // Duration in seconds
  timeSpentMs?: number; // Raw duration in milliseconds from API
  employee?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface StartTimerRequest {
  taskId: string;
  notes?: string;
}

export interface StopTimerRequest {
  entryId: string;
}

export interface TimesheetResponse {
  data: TimesheetEntry[];
  total: number;
} 